// src/components/resources/tools/generators/ScreenshotBeautifier/ScreenshotBeautifierPreview.tsx

'use client'

import { useEffect, useMemo, type CSSProperties } from 'react'
import { useScreenshotBeautifierState, useScreenshotBeautifierActions } from './ScreenshotBeautifierContext'
import { Download, Eye, EyeOff, ZoomIn, ZoomOut, Copy, Check, Image as LucideImage } from 'lucide-react'
import NextImage from 'next/image'
import { ExportButton } from '../shared/ExportButton'
import { warmExportPipeline } from '../shared/exportImage'

/**
 * Preview panel rendering the screenshot with all applied effects.
 * Includes zoom controls, original/enhanced toggle, and export functionality.
 *
 * @returns {JSX.Element} Preview markup
 */
export const ScreenshotBeautifierPreview = (): JSX.Element => {
  const { image, config, zoom, showOriginal, copied, isExporting } = useScreenshotBeautifierState()
  const { setZoom, toggleOriginal, exportImage, copyToClipboard, updateConfig } = useScreenshotBeautifierActions()

  // Warm the export engine's chunk on idle so the first Export isn't a cold
  // dynamic-import stall on the click's critical path. Mobile-safe (no hover);
  // returns a cleanup that cancels a still-pending warm on unmount.
  useEffect(() => warmExportPipeline(), [])
  // Single source of truth: the canonical `export.format` field. Normalize
  // 'jpeg' → 'jpg' and the unsupported 'pdf' → 'png' for the format select.
  const rawFormat = config.export?.format ?? 'png'
  const exportFormat: 'png' | 'jpg' | 'webp' =
    rawFormat === 'jpeg' || rawFormat === 'jpg' ? 'jpg' : rawFormat === 'webp' ? 'webp' : 'png'

  /**
   * Computes the CSS box shadow string from all enabled shadow layers
   */
  const shadowStyle = useMemo(() => {
    return config.shadows
      .filter(s => s.enabled)
      .map(s => `${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${s.color}`)
      .join(', ')
  }, [config.shadows])

  /**
   * Computes the container style with all effects applied
   */
  const containerStyle = useMemo<CSSProperties>(() => {
    const baseStyle: CSSProperties = {
      padding: `${config.padding}px`,
      borderRadius: `${config.borderRadius}px`,
      boxShadow: shadowStyle,
      background: config.background,
      backdropFilter: config.backgroundBlur > 0 ? `blur(${config.backgroundBlur}px)` : undefined,
      border: config.border.enabled ? `${config.border.width}px solid ${config.border.color}` : undefined,
      position: 'relative' as const
    }

    // Apply 3D tilt if enabled
    if (config.tilt.enabled) {
      baseStyle.transform = `perspective(1200px) rotateX(${config.tilt.x}deg) rotateY(${config.tilt.y}deg) scale(${config.scale})`
    } else {
      baseStyle.transform = `scale(${config.scale})`
    }

    // Apply image filters
    const filters = [
      `brightness(${config.filters.brightness}%)`,
      `contrast(${config.filters.contrast}%)`,
      `saturate(${config.filters.saturate}%)`
    ]
    
    if (config.filters.blur > 0) {
      filters.push(`blur(${config.filters.blur}px)`)
    }

    baseStyle.filter = filters.join(' ')

    return baseStyle
  }, [config, shadowStyle])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-300 dark:border-slate-700">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Preview</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleOriginal}
            className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 transition dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {showOriginal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showOriginal ? 'Enhanced' : 'Original'}
          </button>
          <button
            onClick={() => setZoom(Math.max(0.3, zoom - 0.1))}
            className="rounded-lg bg-slate-100 p-1.5 text-slate-700 hover:bg-slate-200 transition dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs text-slate-500 dark:text-slate-400 min-w-[3rem] text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom(Math.min(1.5, zoom + 0.1))}
            className="rounded-lg bg-slate-100 p-1.5 text-slate-700 hover:bg-slate-200 transition dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Preview Area */}
      <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950 p-8">
        <div 
          id="screenshot-beautifier-preview"
          className="flex items-center justify-center min-h-full" 
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
        >
          {image ? (
            <div 
              style={showOriginal ? undefined : containerStyle} 
              className="relative transition-all duration-300"
            >
              {/* Noise overlay */}
              {config.noise.enabled && !showOriginal && (
                <div 
                  className="absolute inset-0 pointer-events-none rounded-[inherit] z-10"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='${config.noise.intensity / 100}' /%3E%3C/svg%3E")`,
                    mixBlendMode: 'overlay'
                  }}
                />
              )}
              <NextImage
                src={image}
                alt="Screenshot"
                className="max-w-full h-auto rounded-[inherit]"
                style={showOriginal ? { maxHeight: '70vh' } : { borderRadius: `${config.borderRadius}px`, maxHeight: '70vh' }}
                width={800}
                height={600}
                unoptimized
                priority
              />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 text-slate-500 dark:text-slate-400">
              <div className="rounded-full border-2 border-dashed border-slate-300 dark:border-slate-700 p-6">
                <LucideImage className="h-12 w-12" aria-hidden="true" />
              </div>
              <div className="text-center">
                <p className="text-base font-medium text-slate-700 dark:text-slate-300">Upload a screenshot</p>
                <p className="text-sm text-slate-500 dark:text-slate-500">Transform it into something beautiful</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export Controls */}
      {image && (
        <div className="p-4 border-t border-slate-300 dark:border-slate-700 flex gap-2">
          <button
            onClick={copyToClipboard}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-slate-100 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-200 transition dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <select
            value={exportFormat}
            onChange={e =>
              updateConfig({ export: { ...config.export, format: e.target.value as 'png' | 'jpg' | 'webp' } })
            }
            aria-label="Export format"
            className="rounded-lg border border-slate-300 bg-white px-2 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
            <option value="webp">WebP</option>
          </select>
          <ExportButton
            isExporting={isExporting}
            onExport={exportImage}
            icon={<Download className="h-4 w-4" />}
            label={`Export ${exportFormat.toUpperCase()}`}
            className="flex-1 rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 transition disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-400"
          />
        </div>
      )}
    </div>
  )
}
